import React, { useState } from 'react';
import {
  CheckCircle,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Eye,
  Zap,
  BarChart2,
  Users,
} from '../../../icons';
import { Button } from '../../ui/button';
import { TruncationTooltip } from '../../common/TruncationTooltip';
import { useIsTruncated } from '../../../lib/useIsTruncated';
import { InstagramIcon, YoutubeIcon } from '../../icons';
import type { VideoResult, AnalysisResult, VerdictV2 } from './types';
import { SC } from './constants';
import { Tag_, RatingBadge, ScoreRing } from './primitives';
import { HighlightedTranscript, CommentsModal } from './modals';
import { ContentCard } from './youtube/content/ContentCard';
import { ChannelAnalysisCard } from './youtube/channel-analysis/ChannelAnalysisCard';

/* ─── TranscriptRow ─────────────────────────────────────────────────────── */

export function TranscriptRow({ video: v, index: i, isInstagram = false }: { video: VideoResult; index: number; isInstagram?: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const ta = v.transcript_analysis;
  const hasT = !!v.transcript;
  const scoreVal = ta?.transcript_safety_score;
  const scoreColor =
    scoreVal != null
      ? scoreVal >= 70
        ? '#059669'
        : scoreVal >= 45
          ? '#d97706'
          : '#dc2626'
      : '#d4d4d4';
  const [titleRef, titleTruncated] = useIsTruncated<HTMLDivElement>(`${i}-${v.title}`);
  const [summaryRef, summaryTruncated] = useIsTruncated<HTMLParagraphElement>(
    ta?.transcript_summary ?? '',
  );

  return (
    <div
      style={{
        border: '1px solid #e5e5e5',
        borderRadius: 14,
        overflow: 'hidden',
        background: '#fff',
      }}
    >
      <div style={{ padding: '13px 16px', display: 'flex', alignItems: 'flex-start', gap: 20 }}>
        {v.thumbnail ? (
          <img
            src={v.thumbnail}
            alt=""
            style={{
              width: 104,
              height: 76,
              objectFit: 'cover',
              flexShrink: 0,
            }}
          />
        ) : (
          <div
            style={{
              width: 104,
              height: 76,
              background: '#f5f5f5',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {isInstagram ? <InstagramIcon size={18} color="#d4d4d4" /> : <YoutubeIcon size={18} color="#d4d4d4" />}
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 9 }}>
          <TruncationTooltip
            isTruncated={titleTruncated}
            content={`#${i + 1} · ${v.title}`}
            contentClassName="max-w-[320px] text-left whitespace-normal"
          >
            <div
              ref={titleRef}
              className="font-medium text-foreground"
              style={{
                fontSize: 20,
                lineHeight: 1.4,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              #{i + 1} · {v.title}
            </div>
          </TruncationTooltip>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {hasT && scoreVal != null && (
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: scoreColor,
                  background: scoreColor + '15',
                  padding: '4px 10px',
                  borderRadius: 99,
                }}
              >
                Score {scoreVal}/100
              </span>
            )}
            {ta && <RatingBadge rating={ta.transcript_flag || 'Unknown'} />}
            {ta && ta.word_count > 0 && (
              <span style={{ fontSize: 14, fontWeight: 500, color: '#737373' }}>
                {ta.word_count.toLocaleString()} words
              </span>
            )}
            {!hasT && (
              <Tag_ color="#d4d4d4" bg="#fafafa" border="#e5e5e5">
                No captions
              </Tag_>
            )}
          </div>
          {ta?.transcript_summary && (
            <TruncationTooltip
              isTruncated={summaryTruncated}
              content={ta.transcript_summary}
              contentClassName="max-w-[320px] text-left whitespace-normal"
            >
              <p
                ref={summaryRef}
                className="text-body-sm-regular text-muted-foreground"
                style={{
                  margin: 0,
                  lineHeight: 1.4,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {ta.transcript_summary}
              </p>
            </TruncationTooltip>
          )}
        </div>
        {hasT && (
          <Button
            variant="ghost"
            onClick={() => setExpanded((e) => !e)}
            style={{ fontSize: 14, fontWeight: 500, color: '#6C09F7', flexShrink: 0, gap: 6 }}
          >
            {expanded ? 'Hide' : 'Show'}
            {expanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
          </Button>
        )}
      </div>
      {expanded && hasT && (
        <div
          style={{
            borderTop: '1px solid #f5f5f5',
            background: '#fafafa',
            padding: '14px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          {/* Sentiment analysis of transcript */}
          {ta?.sentiment_breakdown && (
            <div
              style={{
                background: '#fff',
                border: '1px solid #e5e5e5',
                borderRadius: 12,
                padding: '12px 16px',
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: '#737373',
                  textTransform: 'uppercase',
                  letterSpacing: 0.7,
                  marginBottom: 10,
                }}
              >
                Transcript Sentiment Analysis
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {[
                  { label: 'Positive', key: 'positive_percentage' as const, color: SC.positive },
                  { label: 'Neutral', key: 'neutral_percentage' as const, color: SC.neutral },
                  { label: 'Negative', key: 'negative_percentage' as const, color: SC.negative },
                  { label: 'Toxic', key: 'toxic_percentage' as const, color: SC.toxic },
                ].map((s) => {
                  const val = ta.sentiment_breakdown?.[s.key] ?? 0;
                  return (
                    <div key={s.label}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          marginBottom: 3,
                        }}
                      >
                        <span className="flex items-center text-body-sm-medium text-muted-foreground" style={{ gap: 5 }}>
                          <span
                            style={{
                              width: 7,
                              height: 7,
                              borderRadius: '50%',
                              background: s.color,
                              display: 'inline-block',
                            }}
                          />
                          {s.label}
                        </span>
                        <span style={{ fontSize: 13, fontWeight: 800, color: s.color }}>
                          {val.toFixed(1)}%
                        </span>
                      </div>
                      <div
                        style={{
                          height: 6,
                          background: '#f5f5f5',
                          borderRadius: 3,
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            height: '100%',
                            width: `${Math.min(val, 100)}%`,
                            background: s.color,
                            borderRadius: 3,
                            transition: 'width 0.8s ease',
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Mini stacked bar */}
              <div
                style={{
                  marginTop: 10,
                  height: 8,
                  borderRadius: 4,
                  overflow: 'hidden',
                  display: 'flex',
                  gap: 1,
                }}
              >
                {[
                  { key: 'positive_percentage' as const, color: SC.positive },
                  { key: 'neutral_percentage' as const, color: SC.neutral },
                  { key: 'negative_percentage' as const, color: SC.negative },
                  { key: 'toxic_percentage' as const, color: SC.toxic },
                ].map((s, idx) => {
                  const val = ta.sentiment_breakdown?.[s.key] ?? 0;
                  return val > 0 ? (
                    <div key={idx} style={{ flex: val, background: s.color, minWidth: 2 }} />
                  ) : null;
                })}
              </div>
            </div>
          )}

          {/* Issue tags */}
          {ta &&
            (ta.banned_keyword_matches.length > 0 ||
              ta.sensitive_topic_mentions.length > 0 ||
              ta.problematic_phrases.length > 0) && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {ta.banned_keyword_matches.length > 0 && (
                  <Tag_ color="#dc2626" bg="#fef2f2" border="#fecaca">
                    🚫 {ta.banned_keyword_matches.length} banned kw
                  </Tag_>
                )}
                {ta.sensitive_topic_mentions.length > 0 && (
                  <Tag_ color="#d97706" bg="#fffbeb" border="#fde68a">
                    ⚠ {ta.sensitive_topic_mentions.length} sensitive
                  </Tag_>
                )}
                {ta.problematic_phrases.length > 0 && (
                  <Tag_>{ta.problematic_phrases.length} phrases</Tag_>
                )}
              </div>
            )}

          {/* Transcript text */}
          <div
            style={{
              maxHeight: 260,
              overflowY: 'auto',
              fontSize: 13.5,
              lineHeight: 1.8,
              color: '#404040',
              background: '#fff',
              border: '1px solid #e5e5e5',
              borderRadius: 10,
              padding: '12px 14px',
            }}
          >
            <HighlightedTranscript
              text={v.transcript || ''}
              search=""
              bannedWords={(ta?.banned_keyword_matches || []).map((m) => m.word)}
              sensitiveWords={(ta?.sensitive_topic_mentions || []).map((m) => m.topic)}
              problematicPhrases={(ta?.problematic_phrases || []).map((m) => m.phrase)}
            />
          </div>

          <div
            style={{
              display: 'flex',
              gap: 12,
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                display: 'flex',
                gap: 10,
                fontSize: 10.5,
                color: '#d4d4d4',
                flexWrap: 'wrap',
              }}
            >
              <span>
                <span
                  style={{
                    display: 'inline-block',
                    width: 8,
                    height: 8,
                    background: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: 2,
                    marginRight: 3,
                  }}
                />
                Banned
              </span>
              <span>
                <span
                  style={{
                    display: 'inline-block',
                    width: 8,
                    height: 8,
                    background: '#fffbeb',
                    border: '1px solid #fde68a',
                    borderRadius: 2,
                    marginRight: 3,
                  }}
                />
                Sensitive
              </span>
              <span>
                <span
                  style={{
                    display: 'inline-block',
                    width: 8,
                    height: 8,
                    background: '#f5f5f5',
                    border: '1px solid #e5e5e5',
                    borderRadius: 2,
                    marginRight: 3,
                  }}
                />
                Problematic
              </span>
            </div>
            <a
              href={v.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: '#737373',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <ExternalLink size={11} /> View on YouTube
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── RiskSignalsGrid ────────────────────────────────────────────────────── */

interface RiskSignal {
  signal?: string;
  severity?: string;
  evidence?: string;
}

export function RiskSignalsGrid({ signals }: { signals: unknown }) {
  // Only render signals that actually carry a name — no fabricated "Unknown".
  const list: RiskSignal[] = (Array.isArray(signals) ? (signals as RiskSignal[]) : []).filter(
    (s) => s?.signal,
  );
  if (list.length === 0) return null;
  return (
    <div
      style={{
        background: '#ffffff',
        border: '1px solid #E5E5E5',
        borderRadius: 16,
        padding: 24,
        marginBottom: 16,
      }}
    >
      {/* No Figma frame for this card — styled to the design system (matches the
          sibling Safety/Risk Breakdown card: 20px title, 16px radius, 24px pad). */}
      <div style={{ fontSize: 20, fontWeight: 500, color: '#171717', marginBottom: 18 }}>
        Risk Signals · {list.length}
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 12,
        }}
      >
        {list.map((s, idx) => {
          const severity = String(s?.severity ?? '').toLowerCase();
          const tone =
            severity === 'low' ? { bg: '#ECFDF5', color: '#059669', chip: '#10B981', border: '#D1FAE5' }
            : severity === 'medium' ? { bg: '#FFFBEB', color: '#D97706', chip: '#F59E0B', border: '#FEF3C7' }
            : severity === 'high' ? { bg: '#FEF2F2', color: '#DC2626', chip: '#EF4444', border: '#FEE2E2' }
            : { bg: '#F5F5F5', color: '#404040', chip: '#D4D4D4', border: '#E5E5E5' };
          return (
            <div
              key={`${s?.signal ?? idx}-${idx}`}
              style={{
                border: `1px solid ${tone.border}`,
                borderRadius: 12,
                padding: 14,
                background: '#fff',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                  marginBottom: 6,
                }}
              >
                <div
                  className="text-body-sm-semibold text-foreground"
                  style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {s.signal}
                </div>
                {s?.severity && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      flexShrink: 0,
                      fontSize: 12,
                      fontWeight: 600,
                      textTransform: 'capitalize',
                      padding: '3px 8px',
                      borderRadius: 999,
                      background: tone.bg,
                      color: tone.color,
                    }}
                  >
                    <span
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        background: tone.chip,
                        display: 'inline-block',
                      }}
                    />
                    {s.severity}
                  </span>
                )}
              </div>
              {s?.evidence && (
                <div className="text-caption-regular text-muted-foreground" style={{ lineHeight: 1.5 }}>
                  {s.evidence}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── BrandSafetySkeleton ────────────────────────────────────────────────── */

/** Pulsing placeholder shown while brand safety data is loading or processing.
 *  Matches the visual weight of the real dashboard so there's no layout shift. */
export function BrandSafetySkeleton() {
  const pulse = {
    background: 'linear-gradient(90deg, #f5f5f5 25%, #e5e5e5 50%, #f5f5f5 75%)',
    backgroundSize: '200% 100%',
    animation: 'bsa-shimmer 1.6s ease-in-out infinite',
    borderRadius: 8,
  } as React.CSSProperties;
  const card = {
    background: '#fff',
    border: '1px solid #e5e5e5',
    borderRadius: 14,
    padding: '20px 24px',
  } as React.CSSProperties;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <style>{`@keyframes bsa-shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>

      {/* Score hero */}
      <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 20 }}>
        <div style={{ ...pulse, width: 64, height: 64, borderRadius: '50%', flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <div style={{ ...pulse, height: 18, width: '55%', marginBottom: 10 }} />
          <div style={{ ...pulse, height: 13, width: '38%' }} />
        </div>
        <div style={{ display: 'flex', gap: 32 }}>
          {[1, 2, 3].map((i) => (
            <div key={i} style={{ textAlign: 'center' }}>
              <div style={{ ...pulse, height: 20, width: 52, marginBottom: 6 }} />
              <div style={{ ...pulse, height: 11, width: 44 }} />
            </div>
          ))}
        </div>
      </div>

      {/* Stats row */}
      <div style={{ display: 'flex', gap: 12 }}>
        {[1, 2, 3, 4].map((i) => (
          <div key={i} style={{ ...card, flex: 1, padding: '14px 18px' }}>
            <div style={{ ...pulse, height: 24, width: '60%', marginBottom: 8 }} />
            <div style={{ ...pulse, height: 12, width: '80%' }} />
          </div>
        ))}
      </div>

      {/* Content cards */}
      {[1, 2, 3].map((i) => (
        <div key={i} style={{ ...card, display: 'flex', gap: 16, alignItems: 'flex-start' }}>
          <div style={{ ...pulse, width: 88, height: 60, borderRadius: 8, flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ ...pulse, height: 14, width: '70%', marginBottom: 8 }} />
            <div style={{ ...pulse, height: 12, width: '45%', marginBottom: 10 }} />
            <div style={{ display: 'flex', gap: 8 }}>
              {[1, 2, 3].map((j) => (
                <div key={j} style={{ ...pulse, height: 22, width: 64, borderRadius: 99 }} />
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── ChannelDashboard ───────────────────────────────────────────────────── */

export function ChannelDashboard({
  data,
  hideIdentity = false,
  hideHero = false,
  verdictV2,
}: {
  data: AnalysisResult;
  hideIdentity?: boolean;
  hideHero?: boolean;
  verdictV2?: VerdictV2 | null;
}) {
  const [filterFlag, setFilterFlag] = useState<string | null>(null);
  const [commentVideo, setCommentVideo] = useState<VideoResult | null>(null);

  // Data-readiness gate sits below the hooks (so hook call order stays stable
  // across renders — Rules of Hooks) but above every `data` dereference below,
  // preserving the original null-safety.
  if (!data?.summary || !data?.channel) {
    return <BrandSafetySkeleton />;
  }

  const { channel, summary } = data;
  const overall_score = verdictV2 ? verdictV2.overall_score : data.overall_score;
  const overall_rating = verdictV2?.tier ?? data.overall_rating;
  const videos: VideoResult[] = data.videos ?? [];
  const stats = channel?.stats || {};
  const analytics = channel?.analytics || {};
  const isInstagram = !!data.input?.channel_url?.includes('instagram.com');
  const labels = isInstagram
    ? { subscribers: 'Followers', videos: 'Posts', scanned: 'Posts Scanned', analyzed: 'Posts Analyzed', thisYear: 'This Year', comments: 'Comments' }
    : { subscribers: 'Subscribers', videos: 'Videos', scanned: 'Scanned', analyzed: 'Analyzed', thisYear: 'This Year', comments: 'Comments' };

  const displayVideos = filterFlag ? videos.filter((v) => v.flag === filterFlag) : videos;

  const barData = videos.map((v) => ({
    // Full title, untruncated — SentimentDistributionChart truncates for
    // display and shows the full name in a hover tooltip.
    name: v.title,
    Positive: +(v.sentiment?.positive_percentage || 0).toFixed(1),
    Neutral: +(v.sentiment?.neutral_percentage || 0).toFixed(1),
    Negative: +(v.sentiment?.negative_percentage || 0).toFixed(1),
    Toxic: +(v.sentiment?.toxic_percentage || 0).toFixed(1),
  }));

  const videosWithTranscript = videos.filter(
    (v) => v.transcript_analysis?.transcript_safety_score != null,
  );
  const avgTranscriptSafety =
    videosWithTranscript.length > 0
      ? videosWithTranscript.reduce(
          (s, v) => s + (v.transcript_analysis?.transcript_safety_score ?? 0),
          0,
        ) / videosWithTranscript.length
      : null;

  const radarData = [
    { subject: 'Positive', value: +(summary.avg_positive_pct || 0).toFixed(1), fullMark: 100 },
    {
      subject: 'Low Toxic',
      value: +(100 - (summary.avg_toxic_pct || 0)).toFixed(1),
      fullMark: 100,
    },
    {
      subject: 'Low Negative',
      value: +(100 - (summary.avg_negative_pct || 0)).toFixed(1),
      fullMark: 100,
    },
    { subject: 'Overall Safety', value: overall_score != null ? +overall_score.toFixed(1) : 0, fullMark: 100 },
    {
      subject: 'Transcript Safety',
      value: avgTranscriptSafety != null ? +avgTranscriptSafety.toFixed(1) : 0,
      fullMark: 100,
    },
    {
      subject: 'Neutral Balance',
      value: +(summary.avg_neutral_pct || 0).toFixed(1),
      fullMark: 100,
    },
  ];

  const safetyCompareData = videos
    .map((v) => ({
      // Full title passed through untruncated — the display column CSS-
      // truncates by actual pixel width, gated on a tooltip (see
      // ChannelAnalysisCard's SafetyCompareRow) rather than a fixed char
      // count that clips regardless of how much room is actually available.
      fullTitle: v.title,
      commentSafety: 100 - v.risk_score,
      transcriptSafety: v.transcript_analysis?.transcript_safety_score ?? null,
      flag: v.flag,
      views: v.view_count,
      comments: v.comments_analyzed,
    }))
    .sort((a, b) => a.commentSafety - b.commentSafety);

  const card: React.CSSProperties = {
    background: '#ffffff',
    border: '1px solid #E5E5E5',
    borderRadius: 16,

  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {commentVideo && <CommentsModal video={commentVideo} onClose={() => setCommentVideo(null)} />}

      {/* ── Hero ── */}
      {!hideHero && (
      <div
        style={{
          background: '#ffffff',
          border: '1px solid #E5E5E5',
          borderRadius: 16,
          padding: '20px 24px',

        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
          {!hideIdentity && (
            <div style={{ position: 'relative', flexShrink: 0 }}>
              {channel?.profile_image ? (
                <img
                  src={channel.profile_image}
                  alt=""
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    border: '2px solid #e5e5e5',
                    objectFit: 'cover',
                  }}
                />
              ) : (
                <div
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    background: '#f5f5f5',
                    border: '1px solid #e5e5e5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {isInstagram
                    ? <InstagramIcon size={26} color="#737373" />
                    : <YoutubeIcon size={26} color="#737373" />}
                </div>
              )}
              {channel?.is_verified && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    background: '#1d4ed8',
                    border: '2px solid white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CheckCircle size={11} color="white" />
                </div>
              )}
            </div>
          )}
          <div style={{ flex: 1, minWidth: 200 }}>
            {!hideIdentity && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                <h2
                  className="m-0 text-foreground"
                  style={{
                    fontSize: 20,
                    fontWeight: 500,
                    letterSpacing: '-0.01em',
                  }}
                >
                  {channel?.name || 'Channel'}
                </h2>
                {channel?.is_verified && (
                  <span
                    style={{
                      fontSize: 11,
                      padding: '2px 7px',
                      borderRadius: 4,
                      background: '#eff6ff',
                      color: '#1d4ed8',
                      border: '1px solid #bfdbfe',
                      fontWeight: 600,
                    }}
                  >
                    Verified
                  </span>
                )}
              </div>
            )}
            {!hideIdentity && (
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                {[
                  { icon: <Users size={11} />, label: labels.subscribers, val: stats.subscribers },
                  { icon: <BarChart2 size={11} />, label: labels.videos, val: stats.video_count },
                  {
                    icon: <Eye size={11} />,
                    label: 'Avg Views',
                    val: analytics.average_views_10_videos,
                  },
                  {
                    icon: <Zap size={11} />,
                    label: 'Engagement',
                    val: analytics.average_engagement_rate_10_videos,
                  },
                ]
                  .filter((s) => s.val && s.val !== 'N/A')
                  .map((s) => (
                    <div
                      key={s.label}
                      style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}
                    >
                      <span className="text-muted-foreground">{s.icon}</span>
                      <span className="font-medium text-muted-foreground">{s.label}:</span>
                      <span className="font-semibold text-foreground">{s.val}</span>
                    </div>
                  ))}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <ScoreRing score={overall_score} size={88} />
            <RatingBadge rating={overall_rating} size="md" />
            {verdictV2?.confidence && (
              <span style={{
                fontSize: 10, fontWeight: 700, color: '#737373',
                background: '#f5f5f5', borderRadius: 6,
                padding: '2px 8px', letterSpacing: 0.3,
                textTransform: 'uppercase',
              }}>
                Confidence: {verdictV2.confidence}
              </span>
            )}
          </div>
        </div>
        {verdictV2?.needs_human_review && (
          <div style={{
            marginTop: 12,
            padding: '10px 14px',
            borderRadius: 10,
            background: '#fef3c7',
            border: '1px solid #fcd34d',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
          }}>
            <span style={{ fontSize: 15, flexShrink: 0 }}>⚠️</span>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e' }}>Human review required</div>
              {(verdictV2.review_reasons ?? []).length > 0 && (
                <ul style={{ margin: '4px 0 0', padding: '0 0 0 14px', fontSize: 11, color: '#b45309' }}>
                  {verdictV2.review_reasons.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              )}
            </div>
          </div>
        )}
        <div
          style={{
            marginTop: 14,
            paddingTop: 12,
            borderTop: '1px solid #f5f5f5',
            display: 'flex',
            gap: 6,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: '#d4d4d4',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            Coverage:
          </span>
          <span className="text-caption-medium text-muted-foreground">
            {labels.scanned} <b className="text-foreground">{summary.total_videos_scanned}</b> {isInstagram ? 'posts' : 'videos'}
          </span>
          <span className="text-muted-foreground">·</span>
          <span className="text-caption-medium text-muted-foreground">
            Last 12 months:{' '}
            <b className="text-foreground">
              {summary.videos_last_year ?? summary.total_videos_scanned}
            </b>
          </span>
          <span className="text-muted-foreground">·</span>
          <span className="text-caption-medium text-muted-foreground">
            Deep-{labels.analyzed.toLowerCase()} <b className="text-foreground">{summary.videos_analyzed}</b>
          </span>
          {(summary.detected_keywords || []).length > 0 && (
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginLeft: 4 }}>
              {summary.detected_keywords.slice(0, 8).map((kw) => (
                <span
                  key={kw}
                  style={{
                    fontSize: 11,
                    padding: '1px 7px',
                    borderRadius: 4,
                    background: '#fafafa',
                    color: '#737373',
                    border: '1px solid #e5e5e5',
                    fontWeight: 500,
                  }}
                >
                  {kw}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      )}

      {/* Stat-card KPI strip removed — not in Figma (coverage shown in Safety Checks; sentiment in Channel Analysis). */}

      {/* ── Charts (tabbed) ── */}
      <ChannelAnalysisCard
        card={card}
        summary={summary}
        videos={videos}
        isInstagram={isInstagram}
        barData={barData}
        safetyCompareData={safetyCompareData}
        radarData={radarData}
      />

      {/* ── Video Cards ── */}
      <div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 20,
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <div>
            <div className="text-heading-h6 text-foreground">{isInstagram ? 'Recent Post' : 'Content'}</div>
            <div className="text-body-sm-regular text-muted-foreground" style={{ marginTop: 2 }}>
              Audience, Reach &amp; Reputation Overview
            </div>
          </div>
          {/* Segmented filter — Figma: #F4F4F2 track, white active pill + shadow */}
          <div
            style={{
              display: 'inline-flex',
              gap: 6,
              padding: 4,
              background: '#F4F4F2',
              border: '1px solid #E5E5E5',
              borderRadius: 24,
              flexWrap: 'wrap',
              maxWidth: '100%',
            }}
          >
            {[
              { key: null as string | null, label: 'All' },
              ...(['Safe', 'Moderate', 'Risky'] as const)
                .filter((f) => videos.some((v) => v.flag === f))
                .map((f) => ({ key: f as string | null, label: f })),
            ].map((opt) => {
              const active = filterFlag === opt.key;
              return (
                <button
                  key={opt.label}
                  type="button"
                  onClick={() => setFilterFlag(opt.key)}
                  style={{
                    padding: '4px 16px',
                    borderRadius: 24,
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    background: active ? '#FFFFFF' : 'transparent',
                    color: active ? '#171717' : '#737373',
                    boxShadow: active
                      ? '0px 2px 5px rgba(0,0,0,0.06), 0px 1px 2px rgba(0,0,0,0.1)'
                      : 'none',
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {displayVideos.map((v, i) => (
            <ContentCard
              key={v.video_id || i}
              video={v}
              index={videos.indexOf(v)}
              onViewComments={() => setCommentVideo(v)}
              isInstagram={isInstagram}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
